import { useCallback, useEffect, useMemo, useRef } from 'react'

import classNames from 'classnames'

import { useTranslation } from 'react-i18next'

import {
  useAppDispatch,
  useConversationActorRole,
  usePublishedProjects,
  useUserCulture,
} from '@app/hooks'
import {
  trackReplySelectSearchUsed,
  trackReplySelectSortingChanged,
  trackReplySelectTransactionTypeChanged,
} from '@app/services/tracking'
import { getPublishedProjectsThunk } from '@app/store/publishedProjects'

import {
  ListingCard,
  ListingsSorting,
  Loader,
  Search,
  SortingDropdown,
  TransactionTypeDropdown,
  TRANSACTION_TYPE_QUERY,
  useListingCard,
} from '@lib/components'
import type { TransactionType } from '@lib/components'

import { getNumberFormatted } from '@lib/utils'

import { SEARCH_PLACEHOLDER } from './constants'
import styles from './styles.module.scss'
import { SelectListingContainerProps } from './types'
import { useEntityInfiniteScroll } from './useEntityInfiniteScroll'
import { useScrollbarPresence } from './useScrollbarPresence'

const PROJECT_SORTING_OPTIONS: readonly ListingsSorting[] = [
  ListingsSorting.MostRecent,
  ListingsSorting.LeastRecent,
]

export const SelectProjectContainer = ({
  onSelect,
  searchQuery,
  onSearchChange,
  transactionType,
  onTransactionTypeChange,
  sort,
  onSortChange,
  selectedIds = [],
}: SelectListingContainerProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const conversationActorRole = useConversationActorRole()

  const { projects, fetching, loaded, hasMore, skip, pageSize, total } =
    usePublishedProjects()
  const culture = useUserCulture()
  const { getProjectCardData } = useListingCard()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasScrollBar = useScrollbarPresence(scrollContainerRef, [projects, fetching])
  const transactionTypeQueryValue = transactionType
    ? TRANSACTION_TYPE_QUERY[transactionType]
    : undefined
  const skipNextSortFetchRef = useRef(false)
  const lastTrackedSearchValueRef = useRef(searchQuery)
  const totalProjects = total ?? 0

  useEffect(() => {
    lastTrackedSearchValueRef.current = searchQuery
  }, [searchQuery])

  useEffect(() => {
    if (skipNextSortFetchRef.current) {
      skipNextSortFetchRef.current = false
      return
    }

    dispatch(
      getPublishedProjectsThunk({
        count: pageSize,
        search: searchQuery || undefined,
        transactionType: transactionTypeQueryValue,
        sort,
      })
    )
  }, [dispatch, pageSize, searchQuery, sort, transactionTypeQueryValue])

  const loadPage = useCallback(
    (nextSkip: number) => {
      dispatch(
        getPublishedProjectsThunk({
          skip: nextSkip,
          count: pageSize,
          search: searchQuery || undefined,
          transactionType: transactionTypeQueryValue,
          sort,
        })
      )
    },
    [dispatch, pageSize, searchQuery, transactionTypeQueryValue, sort]
  )

  const { handleScroll } = useEntityInfiniteScroll({
    containerId: 'selectProjectScrollContainer',
    itemsLength: projects.length,
    skip,
    fetching,
    loaded,
    hasMore,
    loadPage,
  })

  const handleSearchChange = useCallback(
    (term: string) => {
      const trimmed = term.trim()

      onSearchChange(trimmed)
      if (trimmed === lastTrackedSearchValueRef.current) {
        return
      }

      trackReplySelectSearchUsed({
        entityType: 'project',
      }, conversationActorRole)
      lastTrackedSearchValueRef.current = trimmed
    },
    [conversationActorRole, onSearchChange]
  )

  const handleTransactionSelect = useCallback(
    (type?: TransactionType) => {
      let nextType: TransactionType | undefined

      if (!type) {
        nextType = undefined
      } else {
        nextType = transactionType === type ? undefined : type
      }

      onTransactionTypeChange(nextType)
      trackReplySelectTransactionTypeChanged({
        entityType: 'project',
        transactionType: nextType ?? null,
      }, conversationActorRole)
    },
    [conversationActorRole, onTransactionTypeChange, transactionType]
  )

  const handleSortChange = useCallback(
    (nextSort: ListingsSorting) => {
      if (totalProjects <= 1) {
        skipNextSortFetchRef.current = true
      }

      onSortChange(nextSort)
      trackReplySelectSortingChanged({
        entityType: 'project',
        sort: nextSort,
      }, conversationActorRole)
    },
    [conversationActorRole, onSortChange, totalProjects]
  )

  const formattedTotalProjects = useMemo(
    () => getNumberFormatted(totalProjects, culture),
    [culture, totalProjects]
  )

  const selectedIdsSet = useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  )

  const projectItems = useMemo(
    () =>
      projects.map((project) => {
        const cardData = getProjectCardData(project)
        const isSelected = selectedIdsSet.has(project.id)
        const cardClassName = classNames({
          [styles.listingCardSelected]: isSelected,
        })

        return {
          id: project.id,
          cardData,
          isSelected,
          cardClassName,
          select: () =>
            onSelect({ id: project.id, type: 'project', cardData }),
        }
      }),
    [getProjectCardData, onSelect, projects, selectedIdsSet]
  )

  const projectScrollClassName = useMemo(
    () =>
      classNames(styles.listingScroll, {
        [styles.listingScrollHasBar]: hasScrollBar,
      }),
    [hasScrollBar]
  )

  return (
    <div className={styles.listingWrapper}>
      <div className={styles.listingsHeaderContainer}>
        <div className={styles.filters}>
          <div className={styles.searchContainer}>
            <Search onDebouncedChange={handleSearchChange} value={searchQuery} placeholder={SEARCH_PLACEHOLDER} />
          </div>
          <TransactionTypeDropdown
            value={transactionType}
            onChange={handleTransactionSelect}
            className={styles.dropdownWrapper}
          />
        </div>
        <div className={styles.sorting}>
          <div className={styles.listingsCount}>
            {t('Chat.Reply.Select.ProjectsCount', {
              count: totalProjects,
              formattedTotalProjects,
            })}
          </div>
          <div className={styles.sortingDropdownContainer}>
            <SortingDropdown
              value={sort}
              onChange={handleSortChange}
              toggleClassName={styles.sortingDropdownToggle}
              options={PROJECT_SORTING_OPTIONS}
            />
          </div>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className={projectScrollClassName}
        onScroll={handleScroll}
        id="selectProjectScrollContainer"
      >
        <div className={styles.listingContainer}>
          {projectItems.map(({ id, cardData, select, cardClassName }) => (
            <ListingCard
              key={id}
              onClick={select}
              showSubtitle
              className={cardClassName}
              {...cardData}
            />
          ))}
        </div>
        {fetching && <Loader />}
      </div>
    </div>
  )
}
